import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import WalletSection from './WalletSection';
import { connectWeb3Auth, getWeb3AuthProvider, isWeb3AuthConnected, isWeb3AuthConfigured, disconnectWeb3Auth } from '../utils/web3Auth';
import { getProviderAndSigner } from '../utils/ethersProvider';
import { requestMetaMaskAccounts, isMetaMaskAvailable, getInjectedWalletAccounts } from '../utils/metamaskMobile';
import { connectWalletConnect, isWalletConnectConfigured, isWalletConnectConnected, getWalletConnectProviderAndSigner, disconnectWalletConnect } from '../utils/walletConnect';
import { getEthPriceInFiat, fiatToEth, ethToFiat, formatEthWithFiatSingle } from '../utils/ethPrice';
import { useCurrency } from '../context/CurrencyContext';
import EthWithFiat from './EthWithFiat';
import RocketLeagueInstructions from './RocketLeagueInstructions';
import AutomationThirdPartyNotice from './AutomationThirdPartyNotice';
import WinnerPayoutGuide from './WinnerPayoutGuide';
import VerificationPlanSection from './VerificationPlanSection';
import './GameForm.css';

const nativeSymbol = process.env.REACT_APP_NATIVE_SYMBOL || 'ETH';

const IconBet = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v12M15 9H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H9" />
  </svg>
);
const IconUser = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconRules = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
  </svg>
);
const IconClipboard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 14l2 2 4-4" />
  </svg>
);
const IconTrophy = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4z" />
    <path d="M7 4V2h10v2M7 8V6a5 5 0 0 1 10 0v2" />
    <path d="M12 12v2M9 21v-2a3 3 0 0 1 6 0v2" />
    <path d="M5 12h2M17 12h2M12 2v2" />
  </svg>
);
const IconArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const IconArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);
const IconInfo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);
const IconGamepad = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 12h4v4H6zM14 6h4v4h-4zM14 18h4v2h-4zM14 18v-4M18 14h2" />
    <path d="M6 12V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" />
  </svg>
);

/* Game-specific icons */
const IconCS2 = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    <path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.34 6.34l1.42 1.42M16.24 16.24l1.42 1.42M6.34 17.66l1.42-1.42M16.24 7.76l1.42-1.42" />
  </svg>
);
const IconDota2 = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);
const IconLoL = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2l2 4 4 .5-3 3.5 1 4-3.5-2-3.5 2 1-4-3-3.5 4-.5L12 2z" />
    <path d="M8 14s-1.5 2-2.5 3c-1 1-2 2-2 2" />
    <path d="M16 14s1.5 2 2.5 3c1 1 2 2 2 2" />
  </svg>
);
const IconRocketLeague = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <ellipse cx="12" cy="15" rx="7" ry="3" />
    <path d="M5 15v2c0 .55 1.5 1 7 1s7-.45 7-1v-2" />
    <path d="M12 10V6M10 8h4" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

function GameIcon({ gameId }) {
  switch (Number(gameId)) {
    case 1: return <IconCS2 />;
    case 2: return <IconDota2 />;
    case 3: return <IconLoL />;
    case 4: return <IconRocketLeague />;
    default: return <IconGamepad />;
  }
}

const STEPS = [
  { id: 'details', label: 'Details', shortLabel: '1' },
  { id: 'bet', label: 'Bet', shortLabel: '2' },
  { id: 'wallet', label: 'Wallet', shortLabel: '3' },
  { id: 'create', label: 'Create', shortLabel: '4' },
];

export default function GameForm({ selectedGameId, games = [], onCancel }) {
  const [selectedId, setSelectedId] = useState(selectedGameId ? String(selectedGameId) : '');
  const [ruleValues, setRuleValues] = useState({});
  const [betEth, setBetEth] = useState('');
  const [fiat, setFiat] = useState({ usd: null, eur: null });
  const fiatCurrency = useCurrency();
  const [creating, setCreating] = useState(false);
  const [createPhase, setCreatePhase] = useState('preparing'); // 'preparing' | 'submitting' | 'confirming'
  const [paymentMethod, setPaymentMethod] = useState(
    isWeb3AuthConfigured() ? 'web3auth' : (isWalletConnectConfigured() ? 'walletconnect' : 'metamask')
  );
  const [connecting, setConnecting] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [playerIdentifiers, setPlayerIdentifiers] = useState({
    steamId: '',
    riotId: '',
    riotRegion: 'americas',
    epicUsername: '',
    faceitId: '',
  });
  const [joinerIdentifiers, setJoinerIdentifiers] = useState({
    steamId: '',
    riotId: '',
    riotRegion: 'americas',
    epicUsername: '',
    faceitId: '',
  });
  const [creatorEmail, setCreatorEmail] = useState('');
  const [creatorEmailNotifyAll, setCreatorEmailNotifyAll] = useState(false);
  const [creatorEmailReceiptsOnly, setCreatorEmailReceiptsOnly] = useState(false);
  const [feeSettings, setFeeSettings] = useState(null);
  const [fiatInputRaw, setFiatInputRaw] = useState('');
  const [fiatInputFocused, setFiatInputFocused] = useState(false);
  const navigate = useNavigate();
  const loadingContentRef = useRef(null);
  const walletSectionRef = useRef(null);
  const stepContentRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [managementPasswordToSave, setManagementPasswordToSave] = useState('');
  const [createdChallengeId, setCreatedChallengeId] = useState(null);
  const [passwordSavedConfirmed, setPasswordSavedConfirmed] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [verificationSettlement, setVerificationSettlement] = useState('api_when_possible');
  const [verificationEvidenceTips, setVerificationEvidenceTips] = useState([]);

  // Update selectedId when selectedGameId prop changes
  useEffect(() => {
    if (selectedGameId) {
      setSelectedId(String(selectedGameId));
    }
  }, [selectedGameId]);

  // When payment method changes, clear wallet (user must reconnect with new method)
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setWalletAddress(null);
  };

  const handlePasswordModalContinue = () => {
    if (!passwordSavedConfirmed || !createdChallengeId) return;
    const pwd = managementPasswordToSave;
    setShowPasswordModal(false);
    setManagementPasswordToSave('');
    setCreatedChallengeId(null);
    setPasswordSavedConfirmed(false);
    setPasswordCopied(false);
    navigate(`/challenge/${createdChallengeId}`, {
      state: { managementPassword: pwd },
    });
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(managementPasswordToSave);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = managementPasswordToSave;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  };

  // Auto-detect already-connected wallet (injected/MetaMask first, then Web3Auth, WalletConnect)
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    const check = async () => {
      try {
        const injectedAccounts = await getInjectedWalletAccounts();
        if (!cancelled && injectedAccounts.length > 0) {
          setPaymentMethod('metamask');
          setWalletAddress(injectedAccounts[0]);
          return;
        }
        if (!cancelled && isWeb3AuthConnected()) {
          const { signer } = await getWeb3AuthProvider();
          const addr = await signer.getAddress();
          if (!cancelled) {
            setPaymentMethod('web3auth');
            setWalletAddress(addr);
          }
          return;
        }
        if (!cancelled && isWalletConnectConnected()) {
          const { signer } = await getWalletConnectProviderAndSigner();
          const addr = await signer.getAddress();
          if (!cancelled) {
            setPaymentMethod('walletconnect');
            setWalletAddress(addr);
          }
        }
      } catch {
        if (!cancelled) setWalletAddress(null);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [selectedId]);

  // Fetch fee settings on mount and refresh periodically
  useEffect(() => {
    const fetchFeeSettings = async () => {
      try {
        const response = await api.get('/fee-settings');
        setFeeSettings(response.data);
      } catch (error) {
        console.error('Error fetching fee settings:', error);
        // Set default from env if API fails (500 bps = 5%)
        const feeBps = Number(process.env.REACT_APP_SITE_FEE_BPS) || 500;
        setFeeSettings({ feePercentage: feeBps / 100, feeRecipient: null });
      }
    };
    
    // Fetch immediately
    fetchFeeSettings();
    
    // Refresh every 30 seconds to get latest fee percentage
    const interval = setInterval(fetchFeeSettings, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Also refresh fee settings when bet amount changes (to ensure latest fee percentage)
  useEffect(() => {
    if (betEth && parseFloat(betEth) > 0) {
      const fetchFeeSettings = async () => {
        try {
          const response = await api.get('/fee-settings');
          setFeeSettings(response.data);
        } catch (error) {
          console.error('Error fetching fee settings:', error);
        }
      };
      fetchFeeSettings();
    }
  }, [betEth]);

  // Fetch ETH price for USD/EUR display
  useEffect(() => {
    getEthPriceInFiat().then(setFiat);
  }, []);

  // Scroll to step content when step changes
  useEffect(() => {
    if (stepContentRef.current) {
      setTimeout(() => {
        stepContentRef.current?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 50);
    }
  }, [currentStep]);

  // Scroll to wallet section when connecting
  useEffect(() => {
    if (connecting && walletSectionRef.current) {
      setTimeout(() => {
        walletSectionRef.current?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [connecting]);

  // Scroll to form content when creating challenge (submitting)
  useEffect(() => {
    if (creating && loadingContentRef.current) {
      setTimeout(() => {
        loadingContentRef.current?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [creating]);

  // Reset to step 0 when game changes
  useEffect(() => {
    setCurrentStep(0);
  }, [selectedId]);

  useEffect(() => {
    setVerificationSettlement('api_when_possible');
    setVerificationEvidenceTips([]);
  }, [selectedId]);

  // When a game is picked, fill defaults
  useEffect(() => {
    if (!Array.isArray(games) || games.length === 0) return;
    const game = games.find(g => g.gameId === Number(selectedId));
    if (!game) return;
    const defaults = {};
    game.ruleFields.forEach(f => {
      defaults[f.key] = f.default ?? (f.type === 'boolean' ? false : '');
    });
    setRuleValues(defaults);
    setBetEth(game.suggestedBetEth || '0.01');
  }, [selectedId, games]);

  // Sync joiner region from creator for LoL (both must be same region)
  useEffect(() => {
    if (Number(selectedId) === 3 && playerIdentifiers.riotRegion) {
      setJoinerIdentifiers(prev => ({
        ...prev,
        riotRegion: playerIdentifiers.riotRegion,
      }));
    }
  }, [selectedId, playerIdentifiers.riotRegion]);

  // Update a rule field
  const handleRuleChange = (key, value, type) => {
    let parsed = value;
    if (type === 'number') parsed = Number(value);
    if (type === 'boolean') parsed = value === 'true' || value === true;
    setRuleValues(prev => ({ ...prev, [key]: parsed }));
  };

  // Connect wallet based on payment method
  const handleConnectWallet = async () => {
    setConnecting(true);
    try {
      if (paymentMethod === 'web3auth') {
        const { signer } = await connectWeb3Auth();
        return await signer.getAddress();
      }

      if (paymentMethod === 'walletconnect') {
        const { signer } = await connectWalletConnect();
        return await signer.getAddress();
      }

      if (paymentMethod === 'metamask') {
        // MetaMask flow - works on desktop and mobile
        const isAvailable = await isMetaMaskAvailable();
        if (!isAvailable) {
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );
          if (isMobile) {
            throw new Error(
              'MetaMask is not installed. Please install MetaMask from the Google Play Store (Android) or App Store (iOS) and try again.'
            );
          } else {
            throw new Error('Please install MetaMask to use this payment method');
          }
        }

        // Request accounts (works on both desktop and mobile)
        const accounts = await requestMetaMaskAccounts();
        if (!accounts || accounts.length === 0) {
          throw new Error('Please connect your MetaMask wallet');
        }
        return accounts[0];
      }
    } catch (err) {
      // Avoid noisy logs in tests/production; surface via UI instead.
      if (process.env.NODE_ENV === 'development') {
        console.error('Wallet connection error:', err);
      }
      throw err;
    } finally {
      setConnecting(false);
    }
  };

  // Submit → creates challenge on‑chain and in DB
  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedId) return alert('Select a game');

    // Validate minimum bet in native token (default 0.001 ETH)
    const minBet = parseFloat(process.env.REACT_APP_MIN_BET_NATIVE || '0.001');
    const betAmount = parseFloat(betEth);
    if (isNaN(betAmount) || betAmount <= 0) {
      return alert('Please enter a valid bet amount');
    }
    if (!isNaN(minBet) && betAmount < minBet) {
      const sym = process.env.REACT_APP_NATIVE_SYMBOL || 'ETH';
      return alert(`Minimum bet is ${minBet} ${sym}`);
    }

    setCreating(true);
    setCreatePhase('preparing');
    let confirmingTimer;
    try {
      // Refresh fee settings right before creating challenge to ensure latest fee percentage
      try {
        const feeResponse = await api.get('/fee-settings');
        setFeeSettings(feeResponse.data);
      } catch (feeError) {
        console.warn('Could not refresh fee settings:', feeError);
        // Continue anyway with cached settings
      }
      
      // Use connected wallet address (user must connect before creating)
      const creatorAddress = walletAddress || (await handleConnectWallet());

      setCreatePhase('submitting');

      // Build player identifiers based on game
      const gameId = Number(selectedId);
      const creatorIdentifiers = {};
      
      if (gameId === 1 || gameId === 2) {
        // CS2 or Dota 2 - Steam ID
        if (playerIdentifiers.steamId) {
          creatorIdentifiers.steamId = playerIdentifiers.steamId.trim();
        }
        if (gameId === 1 && playerIdentifiers.faceitId) {
          // CS2 - optional Faceit ID
          creatorIdentifiers.faceitId = playerIdentifiers.faceitId.trim();
        }
      } else if (gameId === 3) {
        // League of Legends - Riot ID and region
        if (playerIdentifiers.riotId) {
          creatorIdentifiers.riotId = playerIdentifiers.riotId.trim();
        }
        if (playerIdentifiers.riotRegion) {
          creatorIdentifiers.riotRegion = playerIdentifiers.riotRegion;
        }
      } else if (gameId === 4) {
        // Rocket League - Epic Username
        if (playerIdentifiers.epicUsername) {
          creatorIdentifiers.epicUsername = playerIdentifiers.epicUsername.trim();
        }
      }

      // Build joiner identifiers (creator can optionally add opponent's ID; joiner confirms or enters when depositing)
      const joinerIdentifiersBuilt = {};
      if (gameId === 1 || gameId === 2) {
        if (joinerIdentifiers.steamId) joinerIdentifiersBuilt.steamId = joinerIdentifiers.steamId.trim();
        if (gameId === 1 && joinerIdentifiers.faceitId) joinerIdentifiersBuilt.faceitId = joinerIdentifiers.faceitId.trim();
      } else if (gameId === 3) {
        if (joinerIdentifiers.riotId) joinerIdentifiersBuilt.riotId = joinerIdentifiers.riotId.trim();
        if (joinerIdentifiers.riotRegion) joinerIdentifiersBuilt.riotRegion = joinerIdentifiers.riotRegion;
      } else if (gameId === 4) {
        if (joinerIdentifiers.epicUsername) joinerIdentifiersBuilt.epicUsername = joinerIdentifiers.epicUsername.trim();
      }

      const hasCreatorIds = Object.keys(creatorIdentifiers).length > 0;
      const hasJoinerIds = Object.keys(joinerIdentifiersBuilt).length > 0;
      const playerIdentifiersPayload = (hasCreatorIds || hasJoinerIds)
        ? { creator: creatorIdentifiers, joiner: joinerIdentifiersBuilt }
        : undefined;

      const payload = {
        gameId,
        betEth,
        ruleValues,
        creatorAddress,
        paymentMethod,
        creatorEmail: (creatorEmail.trim() && (creatorEmailNotifyAll || creatorEmailReceiptsOnly)) ? creatorEmail.trim() : undefined,
        creatorNotifyPreference: (creatorEmail.trim() && (creatorEmailNotifyAll || creatorEmailReceiptsOnly))
          ? (creatorEmailNotifyAll ? 'all' : 'receipts_only')
          : undefined,
        playerIdentifiers: playerIdentifiersPayload,
        verificationPlan: {
          settlement: verificationSettlement,
          evidenceTips: verificationEvidenceTips,
        },
      };

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out. Please check your connection and try again.')), 60000);
      });

      // After ~8s, show "confirming" phase (blockchain confirmation is typically the slow part)
      confirmingTimer = setTimeout(() => setCreatePhase('confirming'), 8000);

      const { data } = await Promise.race([
        api.post('/challenges', payload),
        timeoutPromise
      ]);

      clearTimeout(confirmingTimer);

      if (!data || !data.challengeId) {
        throw new Error('Invalid response from server');
      }

      // Show management password modal – only shown once; user must save it
      if (data.managementPassword) {
        setCreatedChallengeId(data.challengeId);
        setManagementPasswordToSave(data.managementPassword);
        setShowPasswordModal(true);
        return; // Don't navigate yet – wait for user to confirm they saved it
      }

      // go to challenge page (no password for old flow)
      navigate(`/challenge/${data.challengeId}`);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error creating challenge:', err);
      }
      
      let errorMessage = 'Failed to create challenge. ';
      
      if (err?.response) {
        // Server responded with error
        errorMessage += err.response.data?.error || `Server error: ${err.response.status}`;
      } else if (err?.request) {
        // Request was made but no response received
        errorMessage += 'No response from server. Please check if the backend is running.';
      } else if (err?.message) {
        // Error message from the code
        errorMessage += err.message;
        
        // If Web3Auth error, suggest MetaMask
        if (err.message.includes('Web3Auth') && !err.message.includes('MetaMask')) {
          errorMessage += '\n\n💡 Tip: You can use MetaMask instead by selecting it as your payment method.';
        }
      } else {
        errorMessage += 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    } finally {
      setCreating(false);
      setCreatePhase('preparing');
      if (confirmingTimer) clearTimeout(confirmingTimer);
    }
  };

  const selectedGame = Array.isArray(games) ? games.find(g => g.gameId === Number(selectedId)) : null;

  const minBet = parseFloat(process.env.REACT_APP_MIN_BET_NATIVE || '0.001');

  const canProceedFromStep = (step) => {
    if (step === 0) return true; // Details - no validation
    if (step === 1) {
      const betAmount = parseFloat(betEth);
      return !isNaN(betAmount) && betAmount > 0 && (!Number.isFinite(minBet) || betAmount >= minBet);
    }
    if (step === 2) return !!walletAddress; // Wallet
    return true;
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canProceedFromStep(currentStep)) {
      setCurrentStep(s => s + 1);
    } else if (currentStep === 1) {
      const betAmount = parseFloat(betEth);
      if (isNaN(betAmount) || betAmount <= 0) {
        alert('Please enter a valid bet amount.');
      } else if (betAmount < minBet) {
        alert(`Minimum bet is ${minBet} ${nativeSymbol}`);
      }
    } else if (currentStep === 2 && !walletAddress) {
      alert('Please connect your wallet to continue.');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  // Show form when a game is selected
  if (!selectedId || !selectedGame) {
    return (
      <div className="game-form-container">
        <div className="no-game-selected">
          <p>Please select a game to create a challenge.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="game-form-container">
      <div className="game-form-content" ref={loadingContentRef}>

        {/* Full-screen loading overlay - only when submitting the form */}
        {creating && (
          <div className="form-loading-overlay">
            <div className="form-loading-content">
              <div className="loading-spinner"></div>
              <p className="loading-message">
                {createPhase === 'preparing' && 'Preparing your challenge...'}
                {createPhase === 'submitting' && 'Submitting to blockchain...'}
                {createPhase === 'confirming' && 'Confirming transaction...'}
              </p>
              <p className="loading-submessage">
                Usually takes 30–60 seconds. Please don&apos;t close this page or refresh.
              </p>
              <ul className="loading-steps" aria-live="polite">
                <li className={createPhase === 'preparing' ? 'active' : 'done'}>
                  <span className="loading-step-icon">{createPhase !== 'preparing' ? '✓' : '○'}</span>
                  Preparing your challenge
                </li>
                <li className={createPhase === 'submitting' ? 'active' : createPhase === 'confirming' ? 'done' : ''}>
                  <span className="loading-step-icon">{createPhase !== 'preparing' && createPhase !== 'submitting' ? '✓' : '○'}</span>
                  Submitting to blockchain
                </li>
                <li className={createPhase === 'confirming' ? 'active' : ''}>
                  <span className="loading-step-icon">{createPhase === 'confirming' ? '○' : '○'}</span>
                  Confirming transaction
                </li>
              </ul>
            </div>
          </div>
        )}

        <form className="challenge-form challenge-form-stepper" onSubmit={handleSubmit} style={{ opacity: creating ? 0.5 : 1, pointerEvents: creating ? 'none' : 'auto' }}>
          {/* Progress indicator */}
          <div className="stepper-progress" role="progressbar" aria-valuenow={currentStep + 1} aria-valuemin={1} aria-valuemax={STEPS.length} aria-label={`Step ${currentStep + 1} of ${STEPS.length}`}>
            <div className="stepper-dots">
              {STEPS.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  className={`stepper-dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''} ${i > currentStep ? 'locked' : ''}`}
                  onClick={() => i <= currentStep && setCurrentStep(i)}
                  aria-current={i === currentStep ? 'step' : undefined}
                  aria-label={`${s.label}${i < currentStep ? ' (completed)' : ''}`}
                >
                  <span className="stepper-dot-number">{i < currentStep ? '✓' : i + 1}</span>
                </button>
              ))}
            </div>
            <p className="stepper-label">
              {currentStep === 0 && <span className="stepper-label-icon"><IconRules /></span>}
              {currentStep === 1 && <span className="stepper-label-icon"><IconBet /></span>}
              {currentStep === 2 && <span className="stepper-label-icon"><IconUser /></span>}
              {currentStep === 3 && <span className="stepper-label-icon"><IconClipboard /></span>}
              {STEPS[currentStep].label}
            </p>
          </div>

          {/* Step content - only active step visible */}
          <div className="stepper-content" ref={stepContentRef}>
          {currentStep === 0 && (
          <div className="stepper-step" data-step="details">
          <div className="player-identifiers-section">
            <h4 className="rules-title rules-title-with-icon">
              <span className="rules-title-icon"><IconUser /></span>
              Player Information (for auto-verification)
            </h4>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '1em' }}>
              Provide your game identifier to enable automatic winner detection. This is optional but recommended.
            </p>

            {selectedGame.gameId === 1 && <AutomationThirdPartyNotice gameId={1} />}

            {(selectedGame.gameId === 1 || selectedGame.gameId === 2) && (
              <div className="form-group">
                <label className="form-label">
                  Your Steam ID {selectedGame.gameId === 1 ? '(or Steam Profile URL)' : ''}
                </label>
                <input
                  type="text"
                  value={playerIdentifiers.steamId}
                  onChange={e => setPlayerIdentifiers({
                    ...playerIdentifiers,
                    steamId: e.target.value
                  })}
                  className="form-input"
                  placeholder={selectedGame.gameId === 1 
                    ? "76561198012345678 or https://steamcommunity.com/profiles/76561198012345678"
                    : "76561198012345678"}
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  {selectedGame.gameId === 1 
                    ? "Find your Steam ID at steamid.io or use your profile URL"
                    : "Find your Steam ID at steamid.io"}
                </small>
              </div>
            )}

            {selectedGame.gameId === 1 && (
              <div className="form-group">
                <label className="form-label">Your FACEIT ID (for auto-payout on FACEIT)</label>
                <input
                  type="text"
                  value={playerIdentifiers.faceitId}
                  onChange={e => setPlayerIdentifiers({
                    ...playerIdentifiers,
                    faceitId: e.target.value
                  })}
                  className="form-input"
                  placeholder="Your FACEIT nickname or profile name"
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  Required only if you want <strong>automatic</strong> payout and play on FACEIT. Leave blank if
                  you will confirm the winner together (Steam MM).
                </small>
              </div>
            )}

            {selectedGame.gameId === 3 && (
              <>
                <div className="form-group">
                  <label className="form-label">Your Riot ID</label>
                  <input
                    type="text"
                    value={playerIdentifiers.riotId}
                    onChange={e => setPlayerIdentifiers({
                      ...playerIdentifiers,
                      riotId: e.target.value
                    })}
                    className="form-input"
                    placeholder="PlayerName#TagLine"
                  />
                  <small style={{ color: '#666', fontSize: '0.85em' }}>
                    Format: YourGameName#YourTag (e.g., "Summoner#NA1")
                  </small>
                </div>
                <div className="form-group">
                  <label className="form-label">Region</label>
                  <select
                    value={playerIdentifiers.riotRegion}
                    onChange={e => setPlayerIdentifiers({
                      ...playerIdentifiers,
                      riotRegion: e.target.value
                    })}
                    className="form-input"
                  >
                    <option value="americas">Americas (NA, BR, LAN, LAS)</option>
                    <option value="europe">Europe (EUW, EUNE, RU, TR)</option>
                    <option value="asia">Asia (KR, JP)</option>
                    <option value="sea">Oceania (OCE)</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '0.85em' }}>
                    Select the region where you play. Both players must be in the same region.
                  </small>
                </div>
              </>
            )}

            {selectedGame.gameId === 4 && (
              <div className="form-group">
                <label className="form-label">Your Epic Games display name</label>
                <input
                  type="text"
                  value={playerIdentifiers.epicUsername}
                  onChange={e => setPlayerIdentifiers({
                    ...playerIdentifiers,
                    epicUsername: e.target.value
                  })}
                  className="form-input"
                  placeholder="Your Epic Games display name (exactly as in-game)"
                />
                <RocketLeagueInstructions epicUsername={playerIdentifiers.epicUsername} />
              </div>
            )}
          </div>

          <div className="player-identifiers-section joiner-identifiers-section">
            <h4 className="rules-title rules-title-with-icon">
              <span className="rules-title-icon"><IconUser /></span>
              Opponent&apos;s Player ID (optional)
            </h4>
            <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '1em' }}>
              If you know your opponent&apos;s game ID, add it here. They can confirm or edit it when they deposit.
            </p>
            
            {(selectedGame.gameId === 1 || selectedGame.gameId === 2) && (
              <div className="form-group">
                <label className="form-label">
                  Opponent&apos;s Steam ID {selectedGame.gameId === 1 ? '(or Profile URL)' : ''}
                </label>
                <input
                  type="text"
                  value={joinerIdentifiers.steamId}
                  onChange={e => setJoinerIdentifiers({
                    ...joinerIdentifiers,
                    steamId: e.target.value
                  })}
                  className="form-input"
                  placeholder={selectedGame.gameId === 1 
                    ? "76561198012345678 or https://steamcommunity.com/profiles/..."
                    : "76561198012345678"}
                />
              </div>
            )}

            {selectedGame.gameId === 1 && (
              <div className="form-group">
                <label className="form-label">Opponent&apos;s FACEIT ID (for auto-payout on FACEIT)</label>
                <input
                  type="text"
                  value={joinerIdentifiers.faceitId}
                  onChange={e => setJoinerIdentifiers({
                    ...joinerIdentifiers,
                    faceitId: e.target.value
                  })}
                  className="form-input"
                  placeholder="Their FACEIT nickname"
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  Same as above — only for FACEIT matches + automatic payout.
                </small>
              </div>
            )}

            {selectedGame.gameId === 3 && (
              <>
                <div className="form-group">
                  <label className="form-label">Opponent&apos;s Riot ID</label>
                  <input
                    type="text"
                    value={joinerIdentifiers.riotId}
                    onChange={e => setJoinerIdentifiers({
                      ...joinerIdentifiers,
                      riotId: e.target.value
                    })}
                    className="form-input"
                    placeholder="PlayerName#TagLine"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Region</label>
                  <select
                    value={joinerIdentifiers.riotRegion}
                    onChange={e => setJoinerIdentifiers({
                      ...joinerIdentifiers,
                      riotRegion: e.target.value
                    })}
                    className="form-input"
                  >
                    <option value="americas">Americas (NA, BR, LAN, LAS)</option>
                    <option value="europe">Europe (EUW, EUNE, RU, TR)</option>
                    <option value="asia">Asia (KR, JP)</option>
                    <option value="sea">Oceania (OCE)</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '0.85em' }}>
                    Must match your region
                  </small>
                </div>
              </>
            )}

            {selectedGame.gameId === 4 && (
              <div className="form-group">
                <label className="form-label">Opponent&apos;s Epic Games Username</label>
                <input
                  type="text"
                  value={joinerIdentifiers.epicUsername}
                  onChange={e => setJoinerIdentifiers({
                    ...joinerIdentifiers,
                    epicUsername: e.target.value
                  })}
                  className="form-input"
                  placeholder="Their Epic display name (exactly as in-game)"
                />
              </div>
            )}
          </div>

          <VerificationPlanSection
            gameId={selectedGame.gameId}
            settlement={verificationSettlement}
            onSettlementChange={setVerificationSettlement}
            evidenceTips={verificationEvidenceTips}
            onEvidenceTipsChange={setVerificationEvidenceTips}
          />

          <details
            className="winner-payout-details"
            style={{ marginBottom: '1.25rem', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', background: '#fff' }}
          >
            <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
              How the winner gets paid (automatic + manual) — please read
            </summary>
            <div style={{ marginTop: 12 }}>
              <WinnerPayoutGuide
                gameId={selectedGame.gameId}
                variant="light"
                mode="create"
                autoVerify={verificationSettlement !== 'player_confirm_only'}
                settlementPreference={verificationSettlement}
              />
            </div>
          </details>

          {selectedGame.ruleFields && selectedGame.ruleFields.length > 0 && (
            <div className="rules-section">
              <h4 className="rules-title rules-title-with-icon">
                <span className="rules-title-icon"><IconRules /></span>
                Game-specific rules
              </h4>
              {selectedGame.ruleFields.map(f => {
                const val = ruleValues[f.key] ?? '';
                switch (f.type) {
                  case 'number':
                    return (
                      <div key={f.key} className="form-group">
                        <label className="form-label">{f.label}</label>
                        <input
                          type="number"
                          min={f.min}
                          max={f.max}
                          value={val}
                          onChange={e => handleRuleChange(f.key, e.target.value, 'number')}
                          className="form-input"
                        />
                      </div>
                    );
                  case 'boolean':
                    return (
                      <div key={f.key} className="form-group">
                        <label className="form-label">{f.label}</label>
                        <select
                          value={val}
                          onChange={e => handleRuleChange(f.key, e.target.value, 'boolean')}
                          className="form-input"
                        >
                          <option value={true}>Yes</option>
                          <option value={false}>No</option>
                        </select>
                      </div>
                    );
                  case 'select':
                    return (
                      <div key={f.key} className="form-group">
                        <label className="form-label">{f.label}</label>
                        <select
                          value={val}
                          onChange={e => handleRuleChange(f.key, e.target.value, 'select')}
                          className="form-input"
                        >
                          {f.options.map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                    );
                  default:
                    return (
                      <div key={f.key} className="form-group">
                        <label className="form-label">{f.label}</label>
                        <input
                          type="text"
                          value={val}
                          onChange={e => handleRuleChange(f.key, e.target.value, 'text')}
                          className="form-input"
                        />
                      </div>
                    );
                }
              })}
            </div>
          )}
          </div>
          )}

          {currentStep === 1 && (
          <div className="stepper-step" data-step="bet">
          <div className="form-group">
            <label className="form-label form-label-with-icon" htmlFor="betEthInput">
              <span className="form-label-icon"><IconBet /></span>
              Bet amount ({nativeSymbol})
            </label>
            <input
              id="betEthInput"
              type="number"
              step="0.0001"
              min={process.env.REACT_APP_MIN_BET_NATIVE || '0.001'}
              value={betEth}
              onChange={e => setBetEth(e.target.value)}
              required
              className="form-input"
              placeholder={`Minimum ${process.env.REACT_APP_MIN_BET_NATIVE || '0.001'} ${nativeSymbol}`}
            />
            <small style={{ color: '#666', fontSize: '0.9em', display: 'block' }}>
              Minimum bet: {process.env.REACT_APP_MIN_BET_NATIVE || '0.001'} {nativeSymbol}
            </small>
            {betEth && parseFloat(betEth) > 0 && (() => {
              const fiatText = formatEthWithFiatSingle(betEth, fiat, fiatCurrency);
              return fiatText ? (
                <small style={{ color: '#9e9e9e', fontSize: '0.9em', display: 'block', marginTop: 4 }}>
                  {fiatText}
                </small>
              ) : null;
            })()}

            {(fiat.usd != null || fiat.eur != null) && (
              <div className="fiat-entry-row">
                <span className="fiat-entry-label">Or enter amount in {fiatCurrency.toUpperCase()}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-input fiat-entry-input"
                  placeholder={fiatCurrency === 'usd' ? 'e.g. 10' : 'e.g. 10'}
                  value={fiatInputFocused ? fiatInputRaw : (betEth && parseFloat(betEth) > 0 ? ethToFiat(betEth, fiatCurrency, fiat) : '')}
                  onFocus={() => {
                    setFiatInputFocused(true);
                    setFiatInputRaw(betEth && parseFloat(betEth) > 0 ? ethToFiat(betEth, fiatCurrency, fiat) : '');
                  }}
                  onChange={e => setFiatInputRaw(e.target.value)}
                  onBlur={() => {
                    setFiatInputFocused(false);
                    const v = fiatInputRaw.trim();
                    if (v === '') {
                      setBetEth('');
                      return;
                    }
                    const amount = parseFloat(v.replace(/,/g, ''));
                    const eth = fiatToEth(amount, fiatCurrency, fiat);
                    const minBet = parseFloat(process.env.REACT_APP_MIN_BET_NATIVE || '0.001');
                    if (eth != null && Number.isFinite(eth)) {
                      const clamped = Number.isFinite(minBet) && eth < minBet ? minBet : eth;
                      setBetEth(clamped.toFixed(6).replace(/\.?0+$/, ''));
                    }
                  }}
                  aria-label={`Bet amount in ${fiatCurrency.toUpperCase()}`}
                />
              </div>
            )}
          </div>

          {/* Fee Information Display - fee % from REACT_APP_SITE_FEE_BPS when set, else API */}
          {feeSettings && betEth && parseFloat(betEth) > 0 && (() => {
            const feeBps = process.env.REACT_APP_SITE_FEE_BPS != null && String(process.env.REACT_APP_SITE_FEE_BPS).trim() !== ''
              ? Number(process.env.REACT_APP_SITE_FEE_BPS)
              : null;
            const displayFeePercentage = feeBps != null ? feeBps / 100 : (feeSettings.feePercentage ?? 5);
            return (
            <div className="form-fee-box">
              <h4 className="form-fee-title">
                <span className="form-fee-icon"><IconInfo /></span>
                Platform Fee Information
              </h4>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px',
                color: '#e0e0e0',
                fontSize: '0.95rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Platform Fee:</span>
                  <span style={{ fontWeight: 600, color: '#ffc107' }}>
                    {displayFeePercentage}%
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <span>Your Bet:</span>
                  <span style={{ fontWeight: 500 }}>
                    <EthWithFiat ethAmount={parseFloat(betEth)} fiat={fiat} nativeSymbol={nativeSymbol} />
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <span>Total Pot (2 players):</span>
                  <span style={{ fontWeight: 500 }}>
                    <EthWithFiat ethAmount={parseFloat(betEth) * 2} fiat={fiat} nativeSymbol={nativeSymbol} />
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  paddingTop: '8px',
                  borderTop: '1px solid rgba(255, 193, 7, 0.2)',
                  marginTop: '4px'
                }}>
                  <span>Fee Amount:</span>
                  <span style={{ fontWeight: 600, color: '#ff9800' }}>
                    <EthWithFiat ethAmount={(parseFloat(betEth) * 2) * (displayFeePercentage / 100)} fiat={fiat} nativeSymbol={nativeSymbol} />
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: '#4caf50',
                  marginTop: '4px'
                }}>
                  <span>Winner Receives:</span>
                  <span>
                    <EthWithFiat ethAmount={(parseFloat(betEth) * 2) * (1 - displayFeePercentage / 100)} fiat={fiat} nativeSymbol={nativeSymbol} />
                  </span>
                </div>
              </div>
              <p style={{ 
                margin: '12px 0 0 0', 
                fontSize: '0.85rem', 
                color: '#b0b0b0',
                fontStyle: 'italic'
              }}>
                The platform fee ({displayFeePercentage}%) is deducted from the total pot before payout.
              </p>
            </div>
            );
          })()}
          </div>
          )}

          {currentStep === 2 && (
          <div className="stepper-step" data-step="wallet">
          <WalletSection
            ref={walletSectionRef}
            paymentMethod={paymentMethod}
            walletAddress={walletAddress}
            connecting={connecting}
            onPaymentMethodChange={handlePaymentMethodChange}
            onConnect={async () => {
              setConnecting(true);
              try {
                const addr = await handleConnectWallet();
                setWalletAddress(addr);
              } catch (err) {
                alert(err?.message || 'Failed to connect wallet');
              } finally {
                setConnecting(false);
              }
            }}
            onDisconnect={async () => {
              try {
                if (paymentMethod === 'web3auth') await disconnectWeb3Auth();
                else if (paymentMethod === 'walletconnect') await disconnectWalletConnect();
              } catch (err) {
                if (process.env.NODE_ENV === 'development') console.warn('Disconnect error:', err);
              }
              setWalletAddress(null);
            }}
            creatorEmail={creatorEmail}
            creatorEmailNotifyAll={creatorEmailNotifyAll}
            creatorEmailReceiptsOnly={creatorEmailReceiptsOnly}
            onCreatorEmailChange={setCreatorEmail}
            onCreatorEmailNotifyAllChange={setCreatorEmailNotifyAll}
            onCreatorEmailReceiptsOnlyChange={setCreatorEmailReceiptsOnly}
          />
          </div>
          )}

          {currentStep === 3 && (
          <div className="stepper-step" data-step="create">
          <div className="stepper-payment-note" role="status" style={{
            marginBottom: 20,
            padding: 14,
            backgroundColor: 'rgba(76, 175, 80, 0.12)',
            border: '1px solid rgba(76, 175, 80, 0.35)',
            borderRadius: 8,
            fontSize: '0.95rem',
            color: '#e8f5e9'
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
              <IconInfo />
              No payment yet
            </span>
            <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: '#c8e6c9', lineHeight: 1.45 }}>
              You only pay after everything is set up. Once you create the challenge, you&apos;ll be taken to your challenge page where you can click <strong>&quot;Deposit bet&quot;</strong> to fund your stake.
            </p>
          </div>
          <div className="stepper-review">
            <h4 className="stepper-review-title">
              <span className="stepper-review-icon"><IconClipboard /></span>
              Review your challenge
            </h4>
            <dl className="stepper-review-list">
              <div className="stepper-review-item">
                <dt><span className="stepper-review-dt-icon"><GameIcon gameId={selectedGame?.gameId} /></span> Game</dt>
                <dd>{selectedGame?.name}</dd>
              </div>
              <div className="stepper-review-item">
                <dt><span className="stepper-review-dt-icon"><IconBet /></span> Bet</dt>
                <dd><EthWithFiat ethAmount={parseFloat(betEth)} fiat={fiat} nativeSymbol={nativeSymbol} /></dd>
              </div>
              <div className="stepper-review-item">
                <dt><span className="stepper-review-dt-icon"><IconUser /></span> Wallet</dt>
                <dd>{walletAddress ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}` : '—'}</dd>
              </div>
              <div className="stepper-review-item">
                <dt><span className="stepper-review-dt-icon"><IconClipboard /></span> Winner verification</dt>
                <dd>
                  {verificationSettlement === 'player_confirm_only'
                    ? 'Confirm in the app only (no auto game check)'
                    : 'Try automatic game data check when IDs are verified'}
                  {verificationEvidenceTips.length > 0
                    ? ` · ${verificationEvidenceTips.length} dispute-prep tool${verificationEvidenceTips.length === 1 ? '' : 's'} selected`
                    : ''}
                </dd>
              </div>
            </dl>
          </div>
          </div>
          )}
          </div>

          <div className="form-actions stepper-actions">
            {onCancel && (
              <button 
                type="button"
                onClick={onCancel}
                className="cancel-button"
              >
                Cancel
              </button>
            )}
            {currentStep > 0 ? (
              <button type="button" onClick={handleBack} className="cancel-button cancel-button-with-icon">
                <span className="form-action-icon"><IconArrowLeft /></span>
                Back
              </button>
            ) : null}
            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="submit-button submit-button-with-icon"
                disabled={!canProceedFromStep(currentStep)}
              >
                Next
                <span className="form-action-icon"><IconArrowRight /></span>
              </button>
            ) : (
              <button 
                type="submit"
                disabled={creating || connecting || !selectedId || !walletAddress}
                className="submit-button submit-button-with-icon"
                title={!walletAddress ? 'Connect your wallet first' : undefined}
              >
                <span className="form-action-icon"><IconTrophy /></span>
                {creating ? 'Creating…' : 'Create Challenge'}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Management password modal – shown once after challenge creation */}
      {showPasswordModal && managementPasswordToSave && (
        <div
          className="form-loading-overlay"
          style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="password-modal-title"
        >
          <div
            style={{
              maxWidth: 440,
              width: '90%',
              padding: 24,
              background: '#1a1a1a',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <h2 id="password-modal-title" style={{ margin: '0 0 16px 0', fontSize: 20, color: '#fff' }}>
              Challenge created! Save your management password
            </h2>
            <div
              style={{
                padding: 12,
                marginBottom: 16,
                background: 'rgba(220, 53, 69, 0.2)',
                border: '1px solid #dc3545',
                borderRadius: 8,
                color: '#ff6b6b',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              ⚠️ You cannot retrieve this password later. If you lose it, there is no way for us to recover it. You will not be able to finalize the winner or request a refund without it.
            </div>
            <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#ccc' }}>
              Copy and save this password now. Share it with your opponent if they should be able to manage the challenge (finalize winner or refund).
            </p>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginBottom: 20,
                padding: 12,
                background: 'rgba(0,0,0,0.3)',
                borderRadius: 8,
                fontFamily: 'monospace',
                fontSize: 18,
                letterSpacing: 2,
              }}
            >
              <span style={{ flex: 1, color: '#fff', wordBreak: 'break-all' }}>
                {managementPasswordToSave}
              </span>
              <button
                type="button"
                onClick={handleCopyPassword}
                style={{
                  padding: '8px 16px',
                  background: passwordCopied ? '#22c55e' : '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  whiteSpace: 'nowrap',
                }}
              >
                {passwordCopied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer', fontSize: 14, color: '#ccc' }}>
              <input
                type="checkbox"
                checked={passwordSavedConfirmed}
                onChange={(e) => setPasswordSavedConfirmed(e.target.checked)}
              />
              I have saved this password and understand it cannot be recovered if lost
            </label>
            <button
              type="button"
              onClick={handlePasswordModalContinue}
              disabled={!passwordSavedConfirmed}
              style={{
                width: '100%',
                padding: 14,
                background: passwordSavedConfirmed ? '#ffc107' : '#555',
                color: passwordSavedConfirmed ? '#000' : '#888',
                border: 'none',
                borderRadius: 8,
                cursor: passwordSavedConfirmed ? 'pointer' : 'not-allowed',
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Continue to challenge
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
